import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { PlotData, PlotMouseEvent } from "plotly.js";
import { mapMostCommonDefectsData } from "../../utils/DefectInsightsGraphHelper";
import { DefectInsightFilterBy } from "../../types/DefectInsightsData";
import "./DefectInsights.css";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { selectDefectInsights } from "../../store/selectors/defectInsightsSelectors";
import { resetTableFilters } from "../../store/thunks/filtersThunks";
import LOCAL_TABLE_CONFIG_KEY from "../../types/TableLocalConfigKeys";
import { defaultDefectsFiltersConfig } from "../DefectsTable/Filters/FilterTemplates";
import { DefectTableFields } from "../DefectsTable/DefectTableFields";
import { FilterMatchMode } from "primereact/api";
import { setActiveTab } from "../../store/slices/defectSlice";
import { DefectTabs } from "../../types/DefectTabs";
import { CommonDefectsGraph } from "./CommonDefectsGraph";

type MostCommonDefectsGraphProps = {
    filterBy: DefectInsightFilterBy;
    filterValues: string[] | undefined;
}

export const MostCommonDefectsGraph = (props: MostCommonDefectsGraphProps) => {
    const { filterBy, filterValues } = props;

    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();

    const { filteredMostCommonDefectsData, mostCommonDefectsLoading } = useAppSelector(selectDefectInsights);
    
    const [data, setData] = useState<Partial<PlotData>[]>([]);

    useEffect(() => {
        if (!mostCommonDefectsLoading) {
            setData(mapMostCommonDefectsData(filteredMostCommonDefectsData, filterBy, filterValues));
        }
    }, [filteredMostCommonDefectsData, filterBy, filterValues, mostCommonDefectsLoading]);

    const onClick = useCallback(
        (event: PlotMouseEvent) => {
            if (event.points.length) {
                const { y, data, pointIndex } = event.points[0];
                const typeCode = data.customdata[pointIndex];
                if (y && typeof y === "string") {
                    const tableFilters = {
                        ...defaultDefectsFiltersConfig,
                        [DefectTableFields.STATUS]: {
                            value: null,
                            matchMode: FilterMatchMode.IN
                        },
                        [DefectTableFields.TYPE_CODE]: {
                            value: [typeCode],
                            matchMode: FilterMatchMode.IN
                        }
                    };

                    if (filterValues !== undefined && data.name) {
                        if (filterBy === DefectInsightFilterBy.VESSELS) {
                            tableFilters[DefectTableFields.NAME_FILTER_FIELD] = {
                                value: [data.name],
                                matchMode: FilterMatchMode.CONTAINS
                            }
                        } else {
                            tableFilters[DefectTableFields.TECHNICAL_MANAGER] = {
                                value: [data.name],
                                matchMode: FilterMatchMode.IN
                            }
                        }
                    }

                    dispatch(
                        resetTableFilters(
                            LOCAL_TABLE_CONFIG_KEY.DEFECTS,
                            tableFilters
                        )
                    );

                    dispatch(setActiveTab(DefectTabs.defects));
                }
            }
        },
        [filterBy, filterValues]
    );

    return (
        <div>
            <h2 className="mb-4">{t("defectInsights.mostCommonDefects")}</h2>
            <CommonDefectsGraph
                data={data}
                filterValues={filterValues}
                legendText={t(`defectInsights.${filterBy}`)}
                loading={mostCommonDefectsLoading}
                onClick={onClick}
            />
        </div>
    )
}